/* File: assets/sr-calc-drapery.js */
(function () {
  const U = window.SRCalc && window.SRCalc.utils;

  if (!U) {
    console.error("[sr-calc-drapery] window.SRCalc.utils not found. Ensure sr-calc-core.js loads first.");
    return;
  }

  // ============================================================
  // Shared helpers
  // ============================================================

  let diagramShowLegend = true;

  // Back-compat: older projects used `drop`; new model uses `height`
  function getHeightFromWindow(w, fallback) {
    if (!w || typeof w !== "object") return fallback;
    if (w.height !== undefined && w.height !== null) return w.height;
    if (w.drop !== undefined && w.drop !== null) return w.drop;
    return fallback;
  }

  function escapeXml(s) {
    return String(s || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function formatMoneyFromCents(cents, currency) {
    const value = Number(cents) / 100;
    if (!Number.isFinite(value)) return "";
    if (currency && typeof Intl !== "undefined" && Intl.NumberFormat) {
      try {
        return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(value);
      } catch (e) {
        // Fall through to basic formatting
      }
    }
    const fixed = value.toFixed(2);
    return currency ? `${currency} ${fixed}` : `$${fixed}`;
  }

  function clampPanels12(n, fallback) {
    const x = Number(n);
    if (!Number.isFinite(x)) return fallback;
    return x <= 1 ? 1 : 2;
  }

  function normalizeDimension(value) {
    if (value === undefined || value === null || value === "") return "";
    const dimension = Number(value);
    return Number.isFinite(dimension) && dimension > 0 ? dimension : "";
  }

  function defaultWindow(defaultPanels) {
    return {
      id: U.uuid(),
      name: "Window 1",
      width: "",
      height: "",
      panels: clampPanels12(defaultPanels, 2)
    };
  }

  function resetSignature(state) {
    const g = state.globals || {};
    return JSON.stringify({
      version: state.version,
      type: state.type,
      units: state.units,
      globals: {
        projectName: g.projectName,
        fabricWidth: g.fabricWidth,
        fullness: g.fullness,
        headerAllowance: g.headerAllowance,
        bottomHem: g.bottomHem,
        sideHem: g.sideHem,
        verticalRepeat: g.verticalRepeat,
        bufferPct: g.bufferPct,
        defaultPanels: g.defaultPanels,
        seamAllowance: g.seamAllowance
      },
      windows: (state.windows || []).map(w => ({
        name: w.name,
        width: w.width,
        height: w.height,
        panels: w.panels
      }))
    });
  }

  function normalizeState(defaults) {
    const base = defaults && typeof defaults === "object" ? defaults : {};
    const globalsIn = (base.globals && typeof base.globals === "object") ? base.globals : {};

    const defaultPanels = clampPanels12(globalsIn.defaultPanels, 2);

    const windows = Array.isArray(base.windows) && base.windows.length
      ? base.windows.map((w, idx) => ({
          id: (w && w.id) || U.uuid(),
          name: (w && w.name) || `Window ${idx + 1}`,
          width: normalizeDimension(w && w.width),
          height: normalizeDimension(getHeightFromWindow(w, "")),
          panels: clampPanels12((w && (w.panels ?? w.o_panels)), defaultPanels)
        }))
      : [defaultWindow(defaultPanels)];

    return {
      version: base.version || 1,
      type: "drapery",
      units: base.units || "in",
      globals: {
        // NEW: persisted project name (string)
        projectName: (typeof globalsIn.projectName === "string" ? globalsIn.projectName : "") || "",

        fabricWidth: U.clampNumber(globalsIn.fabricWidth, 54),
        fullness: U.clampNumber(globalsIn.fullness, 2.0),
        headerAllowance: U.clampNumber(globalsIn.headerAllowance, 8),
        bottomHem: U.clampNumber(globalsIn.bottomHem, 8),
        sideHem: U.clampNumber(globalsIn.sideHem, 1.5),
        verticalRepeat: U.clampNumber(globalsIn.verticalRepeat, 0),
        bufferPct: U.clampNumber(globalsIn.bufferPct, 0),

        defaultPanels: defaultPanels,

        // usable width reduction per fabric width (Sailrite has 1" default)
        seamAllowance: U.clampNumber(globalsIn.seamAllowance, 1)
      },
      windows
    };
  }

  // ============================================================
  // URL param parser + apply-to-defaults helper
  // ============================================================

  function getQueryParams() {
    try {
      const sp = new URLSearchParams(window.location.search || "");
      const get = (k) => {
        const v = sp.get(k);
        return (v === null) ? "" : String(v);
      };
      return {
        handle: get("handle"),
        variant: get("variant"),
        width: get("width"),
        vrep: get("vrep"),
        qty: get("qty"),
        price: get("price"),
        currency: get("currency"),
        src: get("src"),
        ga_debug: get("ga_debug"),
        img: get("img")
      };
    } catch (e) {
      return { handle: "", variant: "", width: "", vrep: "", qty: "", price: "", currency: "", src: "", ga_debug: "", img: "" };
    }
  }

  function toNumOrNull(x) {
    const n = Number(x);
    return Number.isFinite(n) ? n : null;
  }

  function applyIncomingParamsToDefaults(defaults, el) {
    const qp = getQueryParams();

    // stash for UI / future add-to-cart work
    el.dataset.srHandle = qp.handle || "";
    el.dataset.srVariant = qp.variant || "";
    el.dataset.srQty = qp.qty || "";
    el.dataset.srPrice = qp.price || "";
    el.dataset.srCurrency = qp.currency || "";
    el.dataset.srSource = qp.src || "";
    el.dataset.srGaDebug = qp.ga_debug || "";
    el.dataset.srImg = qp.img || "";

    if (!defaults || typeof defaults !== "object") return;

    // Ensure globals exists
    defaults.globals = (defaults.globals && typeof defaults.globals === "object") ? defaults.globals : {};

    const w = toNumOrNull(qp.width);
    const r = toNumOrNull(qp.vrep);

    if (w !== null && w > 0) defaults.globals.fabricWidth = w;
    if (r !== null && r >= 0) defaults.globals.verticalRepeat = r;

    // Quick debug (remove later)
    // eslint-disable-next-line no-console
    console.log("[sr-calc] incoming params:", qp, "applied defaults:", {
      fabricWidth: defaults.globals.fabricWidth,
      verticalRepeat: defaults.globals.verticalRepeat
    });
  }

  function renderProductContextHeader(el) {
    const img = el.dataset.srImg || "";
    const qty = el.dataset.srQty || "";
    const handle = el.dataset.srHandle || "";

    // If nothing to show, skip
    if (!img && !qty && !handle) return;

    // Create once
    if (el.querySelector(".sr-calc__productctx")) return;

    const wrap = document.createElement("div");
    wrap.className = "sr-calc__productctx sr-calc__note";
    wrap.innerHTML = `
      <div style="display:flex; gap:12px; align-items:center;">
        ${img ? `<img src="${escapeXml(img)}" alt="" style="width:72px; height:72px; object-fit:cover; border-radius:10px; border:1px solid rgba(0,0,0,0.12);" />` : ""}
        <div style="min-width:0;">
          <div style="font-weight:700; margin-bottom:2px;">Drapery Yardage Calculator</div>
          ${handle ? `<div style="font-size:12px; opacity:0.8; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">${escapeXml(handle)}</div>` : ""}
          ${qty !== "" ? `<div style="font-size:12px; margin-top:4px;"><b>Available:</b> ${escapeXml(qty)} yd</div>` : ""}
        </div>
      </div>
    `;

    // Insert at top of calculator section
    el.prepend(wrap);
  }

  // ============================================================
  // DraperyCalculator (PURE / no DOM)
  // ============================================================

  class DraperyCalculator {
    // First-Fit Decreasing (simple, good enough)
    packPiecesFFD(pieces, binWidth) {
      const sorted = pieces.slice().sort((a, b) => b.width - a.width);
      const bins = [];

      sorted.forEach(p => {
        let placed = false;
        for (const b of bins) {
          if (b.remaining + 1e-9 >= p.width) {
            b.items.push(p);
            b.remaining -= p.width;
            placed = true;
            break;
          }
        }
        if (!placed) {
          bins.push({
            items: [p],
            remaining: Math.max(0, binWidth - p.width)
          });
        }
      });

      return bins;
    }

    computeWindowDetails(w, g) {
      const width = U.clampNumber(w.width, 0);
      const height = U.clampNumber(w.height, 0);

      const fullness = U.clampNumber(g.fullness, 2.0);
      const fabricWidth = U.clampNumber(g.fabricWidth, 54);
      const seamAllowance = Math.max(0, U.clampNumber(g.seamAllowance, 1));
      const usableWidthPerWidth = Math.max(1, fabricWidth - seamAllowance);

      const vRepeat = U.clampNumber(g.verticalRepeat, 0);
      const sideHem = Math.max(0, U.clampNumber(g.sideHem, 0));
      const header = Math.max(0, U.clampNumber(g.headerAllowance, 0));
      const bottom = Math.max(0, U.clampNumber(g.bottomHem, 0));

      const panels = clampPanels12(w.panels, clampPanels12(g.defaultPanels, 2));

      // Required finished width across the window
      const requiredFinishedTotal = width * fullness;

      // Finished width per panel and cut width per panel (add side hems)
      const finishedPerPanel = panels > 0 ? requiredFinishedTotal / panels : 0;
      const cutWidthPerPanel = finishedPerPanel + 2 * sideHem;

      // Fabric widths needed per panel (float)
      const widthsPerPanelFloat = cutWidthPerPanel / usableWidthPerWidth;

      // Rounded cut length (height + hems, rounded to repeat)
      const cutLength = height + header + bottom;
      const cutLengthRounded = (vRepeat && vRepeat > 0) ? U.ceilToMultiple(cutLength, vRepeat) : cutLength;

      // Per-panel breakdown: floor(full widths) + remainder piece width (inches)
      const perPanel = [];
      for (let p = 1; p <= panels; p++) {
        const f = widthsPerPanelFloat;
        const fullWidths = Math.floor(f + 1e-9);
        const remFrac = Math.max(0, f - fullWidths);
        const remainderCutWidth = remFrac * usableWidthPerWidth; // inches (not a full width)
        perPanel.push({ panel: p, fullWidths, remWidth: remainderCutWidth });
      }

      // Full widths across all panels (fixed, cannot share with other window pieces)
      const fullWidthDrops = perPanel.reduce((sum, pp) => sum + pp.fullWidths, 0);

      // Remainder pieces (can be shared across windows of same cut length)
      const remainderPieces = perPanel
        .map(pp => pp.remWidth)
        .filter(x => x > 0.01)
        .map((rw, idx) => ({
          width: rw,
          windowId: w.id,
          windowName: w.name || "Window",
          panel: idx + 1
        }));

      const warnings = [];
      if (width <= 0 || height <= 0) warnings.push("Width and height must be > 0.");
      if (vRepeat > 0 && cutLengthRounded - cutLength >= vRepeat * 0.9) warnings.push("Large repeat waste on this height.");

      return {
        id: w.id,
        name: w.name || "Window",
        width,
        height,
        hasDimensions: width > 0 && height > 0,
        panels,

        fullness,
        fabricWidth,
        seamAllowance,
        usableWidthPerWidth,

        sideHem,
        header,
        bottom,
        verticalRepeat: vRepeat,

        requiredFinishedTotal,
        finishedPerPanel,
        cutWidthPerPanel,
        widthsPerPanelFloat,

        cutLength,
        cutLengthRounded,

        perPanel,
        fullWidthDrops,
        remainderPieces,

        warnings
      };
    }

    // Project-level optimization:
    // - Full-width drops are always counted (they can't share)
    // - Remainder pieces are packed across ALL windows with same cutLengthRounded
    computeProject(state) {
      const g = state.globals;
      const fabricWidth = U.clampNumber(g.fabricWidth, 54);

      const windows = state.windows.map(w => this.computeWindowDetails(w, g));
      const incompleteWindows = windows.filter(w => !w.hasDimensions);

      if (incompleteWindows.length) {
        return {
          windows,
          packedGroups: [],
          subtotalYards: 0,
          totalYards: 0,
          bufferPct: U.clampNumber(g.bufferPct, 0),
          totalDrops: 0,
          naiveById: new Map(),
          isComplete: false,
          incompleteWindows
        };
      }

      // Group remainders by cut length (can't mix different heights in same drop)
      const groups = new Map(); // key: cutLengthRounded -> grp
      windows.forEach(wd => {
        const key = String(U.roundTo(wd.cutLengthRounded, 4));
        if (!groups.has(key)) {
          groups.set(key, {
            cutLengthRounded: wd.cutLengthRounded,
            cutLengthBaseMin: wd.cutLength,
            cutLengthBaseMax: wd.cutLength,
            pieces: [],
            fullDrops: 0
          });
        }
        const grp = groups.get(key);
        grp.cutLengthBaseMin = Math.min(grp.cutLengthBaseMin, wd.cutLength);
        grp.cutLengthBaseMax = Math.max(grp.cutLengthBaseMax, wd.cutLength);
        grp.fullDrops += wd.fullWidthDrops;
        grp.pieces.push(...wd.remainderPieces);
      });

      // Pack each group
      const packedGroups = [];
      let totalDrops = 0;
      let totalInches = 0;

      groups.forEach(grp => {
        const bins = this.packPiecesFFD(grp.pieces, fabricWidth);
        const drops = grp.fullDrops + bins.length;
        totalDrops += drops;
        totalInches += drops * grp.cutLengthRounded;

        packedGroups.push({
          cutLengthRounded: grp.cutLengthRounded,
          cutLengthBaseMin: grp.cutLengthBaseMin,
          cutLengthBaseMax: grp.cutLengthBaseMax,
          fabricWidth,
          fullDrops: grp.fullDrops,
          remainderBins: bins, // each bin is one extra drop of this height
          drops,
          inches: drops * grp.cutLengthRounded
        });
      });

      // Buffer applies to total yardage
      const subtotalYards = totalInches / 36;
      const bufferPct = U.clampNumber(g.bufferPct, 0);
      const totalYards = subtotalYards * (1 + bufferPct / 100);

      // Naive per-window yardage (for transparency)
      const naivePerWindow = windows.map(wd => {
        const bins = this.packPiecesFFD(wd.remainderPieces.map(p => ({ width: p.width })), wd.fabricWidth);
        const drops = wd.fullWidthDrops + bins.length;
        const inches = drops * wd.cutLengthRounded;
        return {
          id: wd.id,
          naiveDrops: drops,
          naiveYards: U.roundTo(inches / 36, 2)
        };
      });

      const naiveById = new Map(naivePerWindow.map(x => [x.id, x]));

      return {
        windows,
        packedGroups,
        subtotalYards: U.roundTo(subtotalYards, 2),
        totalYards: U.roundTo(totalYards, 2),
        bufferPct,
        totalDrops,
        naiveById,
        isComplete: true,
        incompleteWindows: []
      };
    }
  }

  // ============================================================
  // StateStore (single source of truth)
  // ============================================================

  class StateStore {
    constructor(initialState) {
      this._state = initialState;
      this._subs = new Set();
    }

    getState() {
      return this._state;
    }

    subscribe(fn) {
      if (typeof fn !== "function") return () => {};
      this._subs.add(fn);
      return () => this._subs.delete(fn);
    }

    _emit(meta) {
      for (const fn of this._subs) {
        try { fn(this._state, meta || {}); } catch (e) {}
      }
    }

    setGlobal(key, rawVal) {
      const s = this._state;
      const g = s.globals;

      if (key === "projectName") {
        g.projectName = String(rawVal || "");
        this._emit({ kind: "globals", key });
        return;
      }

      if (key === "fullness") {
        g.fullness = U.clampNumber(rawVal, g.fullness);
      } else if (key === "defaultPanels") {
        g.defaultPanels = clampPanels12(rawVal, g.defaultPanels);
      } else {
        g[key] = U.clampNumber(rawVal, g[key]);
      }

      this._emit({ kind: "globals", key });
    }

    setWindow(id, key, rawVal) {
      const s = this._state;
      const w = s.windows.find(x => x.id === id);
      if (!w) return;

      if (key === "name") w.name = String(rawVal || "");
      else if (key === "width" || key === "height") w[key] = normalizeDimension(rawVal);
      else if (key === "panels") w.panels = clampPanels12(rawVal, w.panels);

      this._emit({ kind: "window", id, key });
    }

    addWindow() {
      const s = this._state;
      const n = s.windows.length + 1;
      const w = defaultWindow(s.globals.defaultPanels);
      w.name = `Window ${n}`;
      s.windows.push(w);
      this._emit({ kind: "structure", action: "add-window" });
    }

    delWindow(id) {
      const s = this._state;
      if (s.windows.length <= 1) return;
      s.windows = s.windows.filter(x => x.id !== id);
      this._emit({ kind: "structure", action: "del-window", id });
    }

    resetTo(state) {
      this._state = state;
      this._emit({ kind: "structure", action: "reset" });
    }
  }

  // ============================================================
  // DiagramView (render + tooltip wiring)
  // ============================================================

  class DiagramView {
    renderProjectDiagram(packedGroups) {
      const gapY = 12;
      const gapGroup = 22;
      const margin = 10;

      const usedFill = "var(--sr-calc-used-fill)";
      const leftoverFill = "var(--sr-calc-leftover-fill)";
      const patternWasteFill = "var(--sr-calc-patternwaste-fill)";
      const stroke = "var(--sr-calc-diag-stroke)";
      const textFill = "var(--sr-calc-diag-text)";

      const maxWidthPx = 420;
      const maxDropHeightPx = 220;
      const maxTotalHeightPx = 1600;

      const legendGap = 14;
      const legendW = 280;
      const fontSize = 12;
      const lineH = 14;

      let totalHeight = margin;

      function makeWasteLabel(wasteMin, wasteMax) {
        if (wasteMax <= 0.01) return "";
        if (Math.abs(wasteMax - wasteMin) <= 0.02) return `Pattern match waste ${U.roundTo(wasteMax, 2)}"`;
        return `Pattern match waste ${U.roundTo(wasteMin, 2)}–${U.roundTo(wasteMax, 2)}"`;
      }

      function estimateTextWidth(text, fontSize) {
        const avgChar = fontSize * 0.56;
        return text.length * avgChar;
      }

      const groupLayouts = packedGroups
        .slice()
        .sort((a, b) => b.cutLengthRounded - a.cutLengthRounded)
        .map(grp => {
          const W = grp.fabricWidth;
          const H = grp.cutLengthRounded;

          const sW = Math.min(6, Math.max(2.5, maxWidthPx / W));
          const sH = Math.min(2.2, maxDropHeightPx / H);

          const dropW = W * sW;
          const dropH = H * sH;

          const baseMin = (typeof grp.cutLengthBaseMin === "number") ? grp.cutLengthBaseMin : H;
          const baseMax = (typeof grp.cutLengthBaseMax === "number") ? grp.cutLengthBaseMax : H;

          const wasteMax = Math.max(0, H - baseMin);
          const wasteMin = Math.max(0, H - baseMax);

          const wasteHpx = wasteMax * sH;
          const usedHpx = (H - wasteMax) * sH;

          const wasteLabel = makeWasteLabel(wasteMin, wasteMax);
          const headerLine =
            `Cut length ${U.roundTo(H, 2)}" — ${grp.drops} drops (${grp.fullDrops} full + ${grp.remainderBins.length} remainder)` +
            (wasteLabel ? ` • ${wasteLabel}` : "");
          const headerW = estimateTextWidth(headerLine, 13);

          const groupHeaderH = 34;
          const groupH = groupHeaderH + grp.drops * (dropH + gapY) + gapGroup;

          totalHeight += groupH;

          return {
            grp,
            sW,
            sH,
            dropW,
            dropH,
            groupHeaderH,
            groupH,
            wasteHpx,
            usedHpx,
            wasteLabel,
            headerLine,
            headerW
          };
        });

      // Scale down if too tall
      if (totalHeight > maxTotalHeightPx) {
        const factor = maxTotalHeightPx / totalHeight;
        groupLayouts.forEach(gl => {
          gl.sH = Math.max(0.35, gl.sH * factor);
          gl.dropH = gl.grp.cutLengthRounded * gl.sH;

          const H = gl.grp.cutLengthRounded;
          const baseMin = (typeof gl.grp.cutLengthBaseMin === "number") ? gl.grp.cutLengthBaseMin : H;
          const baseMax = (typeof gl.grp.cutLengthBaseMax === "number") ? gl.grp.cutLengthBaseMax : H;

          const wasteMax = Math.max(0, H - baseMin);
          const wasteMin = Math.max(0, H - baseMax);

          gl.wasteHpx = wasteMax * gl.sH;
          gl.usedHpx = (H - wasteMax) * gl.sH;
          gl.wasteLabel = makeWasteLabel(wasteMin, wasteMax);
        });

        totalHeight = margin;
        groupLayouts.forEach(gl => {
          totalHeight += gl.groupHeaderH + gl.grp.drops * (gl.dropH + gapY) + gapGroup;
        });
      }

      const maxDropW = Math.max(...groupLayouts.map(gl => gl.dropW));
      const maxHeaderW = Math.max(...groupLayouts.map(gl => gl.headerW || 0));
      const extraW = diagramShowLegend ? (legendGap + legendW) : 0;

      const svgW = Math.round(Math.max(maxDropW + extraW + margin * 2, maxHeaderW + margin * 2));
      const svgH = Math.round(totalHeight + margin);

      let y = margin;
      let svgContent = "";

      const esc = (s) => escapeXml(s);

      groupLayouts.forEach(gl => {
        const { grp, sW, dropW, dropH, groupHeaderH, wasteHpx, usedHpx, wasteLabel } = gl;
        const W = grp.fabricWidth;
        const H = grp.cutLengthRounded;

        svgContent += `
          <text x="${margin}" y="${y + 16}" font-size="13" fill="${textFill}">
            ${esc(gl.headerLine)}
          </text>
        `;

        y += groupHeaderH;

        // FULL drops
        for (let i = 0; i < grp.fullDrops; i++) {
          const x0 = margin;

          svgContent += `
            <rect x="${x0}" y="${y}" width="${dropW}" height="${dropH}" fill="${usedFill}" stroke="${stroke}" />
            ${wasteHpx > 0.75 ? `
              <rect x="${x0}" y="${y + usedHpx}" width="${dropW}" height="${wasteHpx}" fill="${patternWasteFill}" stroke="none" pointer-events="none" />
              ${(wasteHpx >= 8 && wasteLabel) ? `<text x="${x0 + 6}" y="${y + usedHpx + 12}" font-size="${fontSize}" fill="${textFill}" opacity="0.95">${esc(wasteLabel)}</text>` : ""}
            ` : ""}
            <text x="${x0 + 6}" y="${y + 16}" font-size="${fontSize}" fill="${textFill}">
              ${esc(H)} high x ${esc(W)} wide — full width
            </text>
          `;

          y += dropH + gapY;
        }

        // Remainder bins
        grp.remainderBins.forEach((bin, binIdx) => {
          const x0 = margin;
          const legendX = x0 + dropW + (diagramShowLegend ? legendGap : 0);

          svgContent += `
            <rect x="${x0}" y="${y}" width="${dropW}" height="${dropH}" fill="${leftoverFill}" stroke="${stroke}" />
            ${wasteHpx > 0.75 ? `
              <rect x="${x0}" y="${y + usedHpx}" width="${dropW}" height="${wasteHpx}" fill="${patternWasteFill}" stroke="none" pointer-events="none" />
              ${(wasteHpx >= 8 && wasteLabel) ? `<text x="${x0 + 6}" y="${y + usedHpx + 12}" font-size="${fontSize}" fill="${textFill}" opacity="0.95">${esc(wasteLabel)}</text>` : ""}
            ` : ""}
            <text x="${x0}" y="${y - 4}" font-size="${fontSize}" fill="${textFill}">
              Remainder drop ${binIdx + 1} — leftover ${U.roundTo(bin.remaining, 2)}"
            </text>
          `;

          let x = x0;
          bin.items.forEach(it => {
            const wpx = it.width * sW;
            svgContent += `
              <rect data-diag="seg"
                data-tip="${esc(`${U.roundTo(it.width, 2)}\" — ${it.windowName} (panel ${it.panel}), cut ${U.roundTo(H, 2)}\"`)}"
                x="${x}" y="${y}" width="${wpx}" height="${dropH}" fill="${usedFill}" stroke="${stroke}" />
            `;
            x += wpx;
          });

          if (diagramShowLegend) {
            const maxLines = Math.max(1, Math.floor((dropH - 10) / lineH));
            const items = bin.items;

            const lines = [];
            for (let i = 0; i < items.length && lines.length < maxLines; i++) {
              const it = items[i];
              lines.push(`${it.windowName} (panel ${it.panel}) — ${U.roundTo(it.width, 2)}"`);
            }
            const more = items.length - lines.length;
            if (more > 0) lines[lines.length - 1] = `+${more} more…`;

            svgContent += `<g class="sr-calc__svglegend">`;
            lines.forEach((txt, i) => {
              svgContent += `
                <text x="${legendX}" y="${y + 16 + i * lineH}" font-size="${fontSize}" fill="${textFill}">
                  ${esc(txt)}
                </text>
              `;
            });
            svgContent += `</g>`;
          }

          y += dropH + gapY;
        });

        y += gapGroup;
      });

      return `
        <div class="sr-calc__scroll sr-calc__diagramwrap">
          <div class="sr-calc__diagrambar">
            <div class="sr-calc__key" aria-label="Diagram color key">
              <span class="sr-calc__keyitem"><span class="sr-calc__swatch sr-calc__swatch--used"></span> Used</span>
              <span class="sr-calc__keyitem"><span class="sr-calc__swatch sr-calc__swatch--left"></span> Leftover</span>
              <span class="sr-calc__keyitem"><span class="sr-calc__swatch sr-calc__swatch--waste"></span> Pattern match waste</span>
              <span class="sr-calc__keyitem">Tip: hover a cyan block for details</span>
            </div>
          </div>

          <div class="sr-calc__note sr-calc__note--tight">
            <b>Optimized cut diagram</b> (remainders packed across all windows with the same cut length).
          </div>

          <div class="sr-calc__tip" data-diag="tip" aria-hidden="true"></div>

          <svg width="${svgW}" height="${svgH}" viewBox="0 0 ${svgW} ${svgH}" role="img" aria-label="Optimized cut plan">
            ${svgContent}
          </svg>
        </div>
      `;
    }

    bindTooltips(root) {
      const tipEl = root.querySelector('[data-diag="tip"]');
      if (!tipEl) return;

      const segs = root.querySelectorAll('[data-diag="seg"]');
      const show = (e) => {
        const t = e.target && e.target.getAttribute && e.target.getAttribute("data-tip");
        if (!t) return;
        tipEl.textContent = t;
        tipEl.classList.add("is-on");
      };
      const move = (e) => {
        tipEl.style.left = (e.clientX + 12) + "px";
        tipEl.style.top = (e.clientY + 12) + "px";
      };
      const hide = () => tipEl.classList.remove("is-on");

      segs.forEach(seg => {
        seg.addEventListener("pointerenter", show);
        seg.addEventListener("pointermove", (e) => { show(e); move(e); });
        seg.addEventListener("pointerleave", hide);
      });
    }
  }


  // ============================================================
  // Views (Globals / Windows / Results)
  // ============================================================

  class GlobalsView {
    constructor(root, store, onResultsOnly) {
      this.root = root;
      this.store = store;
      this.onResultsOnly = typeof onResultsOnly === "function" ? onResultsOnly : function () {};
    }


    render() {
      const state = this.store.getState();
      const g = state.globals;

      this.root.innerHTML = `
        <div class="sr-calc__form">

          <!-- NEW -->
          <div class="sr-calc__field">
            <label>Project name</label>
            <input type="text" data-g="projectName" value="${escapeXml(g.projectName || "")}" placeholder="e.g., Living room drapes">
            <div class="sr-calc__hint">Used when exporting the project file name.</div>
          </div>
    
          <div class="sr-calc__field">
            <label>Fabric width (in)</label>
            <input type="number" step="0.1" min="1" data-g="fabricWidth" value="${g.fabricWidth}">
            <div class="sr-calc__hint">Common: 54", sheers often 110–118".</div>
          </div>

          <div class="sr-calc__field">
            <label>Fullness (x)</label>
            <select data-g="fullness">
              ${[1.25, 1.5, 2.0, 2.5, 3.0].map(x => `<option value="${x}" ${Number(g.fullness) === x ? "selected" : ""}>${x}×</option>`).join("")}
            </select>
            <div class="sr-calc__hint">Total finished width = Window width × fullness.</div>
          </div>

          <div class="sr-calc__field">
            <label>Header allowance (in)</label>
            <input type="number" step="0.1" min="0" data-g="headerAllowance" value="${g.headerAllowance}">
          </div>

          <div class="sr-calc__field">
            <label>Bottom hem allowance (in)</label>
            <input type="number" step="0.1" min="0" data-g="bottomHem" value="${g.bottomHem}">
          </div>

          <div class="sr-calc__field">
            <label>Side hems (each side, in)</label>
            <input type="number" step="0.1" min="0" data-g="sideHem" value="${g.sideHem}">
          </div>

          <div class="sr-calc__field">
            <label>Seam allowance / usable width loss (in)</label>
            <input type="number" step="0.1" min="0" data-g="seamAllowance" value="${g.seamAllowance}">
            <div class="sr-calc__hint">Usable width per fabric width = Fabric width − seam allowance.</div>
          </div>

          <div class="sr-calc__field">
            <label>Vertical pattern repeat (in)</label>
            <input type="number" step="0.1" min="0" data-g="verticalRepeat" value="${g.verticalRepeat}">
            <div class="sr-calc__hint">0 = no repeat. Cut length is rounded up to repeat.</div>
          </div>

          <div class="sr-calc__field">
            <label>Buffer (%)</label>
            <input type="number" step="0.1" min="0" data-g="bufferPct" value="${g.bufferPct}">
            <div class="sr-calc__hint">Optional safety margin (e.g., 10%).</div>
          </div>

          <div class="sr-calc__field">
            <label>Default finished panels per window</label>
            <select data-g="defaultPanels">
              <option value="2" ${Number(g.defaultPanels) === 2 ? "selected" : ""}>2 panels (left + right)</option>
              <option value="1" ${Number(g.defaultPanels) === 1 ? "selected" : ""}>1 panel (single)</option>
            </select>
          </div>
        </div>

        <div class="sr-calc__note">
          Multi-window projects are <b>optimized</b> by packing remainder widths across windows with the same cut length (reduces waste).
        </div>
      `;

      this._bind();
    }

    _bind() {
      this.root.querySelectorAll("[data-g]").forEach(el => {
        el.addEventListener("input", () => {
          const key = el.getAttribute("data-g");
          this.store.setGlobal(key, el.value);
          this.onResultsOnly(); // results only (prevents focus loss)
        });
      });
    }
  }

  class WindowsView {
    constructor(root, store, onResultsOnly, onDel) {
      this.root = root;
      this.store = store;
      this.onResultsOnly = typeof onResultsOnly === "function" ? onResultsOnly : function () {};
      this.onDel = typeof onDel === "function" ? onDel : function () {};
    }

    render() {
      const state = this.store.getState();
      const g = state.globals;

      const rows = state.windows.map((w, idx) => {
        const name = (w.name || `Window ${idx + 1}`).replace(/"/g, "&quot;");
        const panelsValue = clampPanels12(w.panels, clampPanels12(g.defaultPanels, 2));

        return `
          <div class="sr-calc__itemrow" data-item-row data-id="${w.id}">
            <div class="sr-calc__itemtop sr-calc__itemtop--drapery">
              <div class="sr-calc__itemfield sr-calc__field">
                <label>Name</label>
                <input type="text" data-w="name" value="${name}" class="sr-calc__input-full">
              </div>
              <div class="sr-calc__itemfield sr-calc__field">
                <label>Width (in)</label>
                <input type="number" step="0.1" min="0.1" data-w="width" value="${w.width}" placeholder="Enter width" required>
              </div>
              <div class="sr-calc__itemfield sr-calc__field">
                <label>Height (in)</label>
                <input type="number" step="0.1" min="0.1" data-w="height" value="${w.height}" placeholder="Enter height" required>
              </div>
              <div class="sr-calc__itemfield sr-calc__field">
                <label>Panels</label>
                <select data-w="panels">
                  <option value="2" ${panelsValue === 2 ? "selected" : ""}>2</option>
                  <option value="1" ${panelsValue === 1 ? "selected" : ""}>1</option>
                </select>
              </div>
              <div class="sr-calc__itemfield sr-calc__field">
                <label>Actions</label>
                <div class="sr-calc__rowactions">
                  <button class="sr-calc__smallbtn" type="button" data-act="del">Del</button>
                </div>
              </div>
            </div>
          </div>
        `;
      }).join("");

      this.root.innerHTML = `
        <div class="sr-calc__itemslist">
          ${rows}
        </div>

        <div class="sr-calc__note">
          Panels = finished curtain panels for the window (typically 2: left + right).
        </div>
      `;

      this._bind();
    }

    _bind() {
      const state = this.store.getState();

      this.root.querySelectorAll("[data-item-row]").forEach(tr => {
        const id = tr.getAttribute("data-id");
        const w = state.windows.find(x => x.id === id);
        if (!w) return;

        tr.querySelectorAll("[data-w]").forEach(el => {
          el.addEventListener("input", () => {
            const key = el.getAttribute("data-w");
            this.store.setWindow(id, key, el.value);
            this.onResultsOnly(); // results only (prevents focus loss)
          });
        });

        const btnDel = tr.querySelector('[data-act="del"]');
        if (btnDel) btnDel.addEventListener("click", () => this.onDel(id));
      });
    }
  }

// ============================================================
// ResultsView (table on desktop, cards on mobile)
// ============================================================

// ============================================================
// ResultsView (desktop table + mobile cards)
// Desktop order: Window | Cut length | Est. yards | Panels | Widths/panel | Notes
// Mobile cards: row1 = Cut length | Est. yards, row2 = Widths/panel | Fullness
// ============================================================

  class ResultsView {
    constructor(root, diagramView) {
      this.root = root;
      this.diagramView = diagramView;
    }

    render(project) {
      if (!project.isComplete) {
        const names = project.incompleteWindows.map(windowItem => escapeXml(windowItem.name)).join(", ");
        this.root.innerHTML = `
          <div class="sr-calc__note sr-calc__note--sp12" role="status">
            <b>Enter the width and height</b> for ${names || "each window"} to calculate the required yardage.
          </div>
        `;
        return;
      }

      // --------------------------
      // Desktop table rows
      // --------------------------
      const rowsHtml = project.windows.map(wd => {
        const naive = project.naiveById.get(wd.id);
        const naiveYd = naive ? naive.naiveYards.toFixed(2) : "—";

        const warn = (wd.warnings && wd.warnings.length)
          ? `<div class="sr-calc__warn">${wd.warnings.join(" ")}</div>`
          : "";

        return `
          <tr>
            <td data-label="Window">${escapeXml(wd.name)}</td>
            <td data-label="Cut length">${U.roundTo(wd.cutLengthRounded, 2)} in</td>
            <td data-label="Est. yards">${naiveYd} yd</td>
            <td data-label="Panels">${wd.panels}</td>
            <td data-label="Widths / panel">${U.roundTo(wd.widthsPerPanelFloat, 2)} widths / panel</td>
            <td data-label="Notes">${warn}</td>
          </tr>
        `;
      }).join("");

      // --------------------------
      // Mobile cards
      // --------------------------
      const cardsHtml = project.windows.map(wd => {
        const naive = project.naiveById.get(wd.id);
        const naiveYd = naive ? naive.naiveYards.toFixed(2) : "—";

        const notesHtml = (wd.warnings && wd.warnings.length)
          ? `<div class="sr-calc__warn">${wd.warnings.join(" ")}</div>`
          : `<div class="sr-calc__muted">—</div>`;

        return `
          <div class="sr-calc__rcard">
            <div class="sr-calc__rcardhead">
              <div class="sr-calc__rcardtitle">${escapeXml(wd.name)}</div>
              <div class="sr-calc__rcardmeta">${wd.panels} panel${wd.panels === 1 ? "" : "s"}</div>
            </div>

            <div class="sr-calc__rgrid">
              <!-- row 1 -->
              <div class="sr-calc__ritem">
                <div class="sr-calc__rlabel">Cut length</div>
                <div class="sr-calc__rvalue">${U.roundTo(wd.cutLengthRounded, 2)} in</div>
              </div>

              <div class="sr-calc__ritem">
                <div class="sr-calc__rlabel">Est. yards</div>
                <div class="sr-calc__rvalue">${naiveYd} yd</div>
              </div>

              <!-- row 2 -->
              <div class="sr-calc__ritem">
                <div class="sr-calc__rlabel">Widths / panel</div>
                <div class="sr-calc__rvalue">${U.roundTo(wd.widthsPerPanelFloat, 2)}</div>
              </div>

              <div class="sr-calc__ritem">
                <div class="sr-calc__rlabel">Fullness</div>
                <div class="sr-calc__rvalue">${U.roundTo(wd.fullness, 2)}×</div>
              </div>
            </div>

            <div class="sr-calc__rnotes">
              <div class="sr-calc__rlabel">Notes</div>
              ${notesHtml}
            </div>
          </div>
        `;
      }).join("");

      const diagram = this.diagramView.renderProjectDiagram(project.packedGroups);

      // --------------------------
      // Render
      // --------------------------
      this.root.innerHTML = `
        <!-- Desktop table -->
        <div class="sr-calc__scroll sr-calc__results-table">
          <table class="sr-calc__table">
            <thead>
              <tr>
                <th class="sr-calc__th-name">Window</th>
                <th>Cut length</th>
                <th>Est. yards</th>
                <th>Panels</th>
                <th>Widths / panel</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>${rowsHtml}</tbody>
          </table>
        </div>

        <!-- Mobile cards -->
        <div class="sr-calc__results-cards">
          ${cardsHtml}
        </div>

        <div class="sr-calc__cta-box sr-calc__note sr-calc__note--sp12 sr-calc__results-summary">
          <b>Optimized total</b> (remainders packed across windows):
          <span class="sr-calc__total">${project.subtotalYards.toFixed(2)} yd</span>
          ${project.bufferPct > 0
            ? ` &nbsp;|&nbsp; Buffer ${project.bufferPct}% → <span class="sr-calc__total">${project.totalYards.toFixed(2)} yd</span>`
            : ""
          }
        </div>

        <div class="sr-calc__note sr-calc__note--sp6 sr-calc__results-summary">
          Total drops: <b>${project.totalDrops}</b> (grouped by cut length).
        </div>

        ${diagram}
      `;

      this.diagramView.bindTooltips(this.root);
    }
  }
  // ============================================================
  // Import/Export service
  //    - exportJson() uses project name as filename
  //    - importJson() restores projectName from file
  // ============================================================

  class ImportExportService {
    constructor(store, defaults) {
      this.store = store;
      this.defaults = defaults;
    }

    // NEW helper inside the class (or place it as a shared helper outside)
    _sanitizeFileStem(name) {
      const s = String(name || "").trim();
      if (!s) return "sr-drapery-project";
      return s
        .replace(/[\/\\?%*:|"<>]/g, "-")   // Windows-illegal chars
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 80);
    }

    exportJson() {
      const state = this.store.getState();

      const payload = {
        version: state.version,
        type: state.type,
        units: state.units,
        // Export only room + drapery design info (exclude fabric-specific fields)
        globals: {
          projectName: state.globals.projectName,
          fullness: state.globals.fullness,
          headerAllowance: state.globals.headerAllowance,
          bottomHem: state.globals.bottomHem,
          sideHem: state.globals.sideHem,
          bufferPct: state.globals.bufferPct,
          defaultPanels: state.globals.defaultPanels
        },
        windows: state.windows.map(w => ({
          id: w.id,
          name: w.name,
          width: w.width,
          height: w.height,
          panels: clampPanels12(w.panels, clampPanels12(state.globals.defaultPanels, 2))
        }))
      };

      const stem = this._sanitizeFileStem(state.globals && state.globals.projectName);
      window.SRCalc.utils.downloadJson(`${stem}.json`, payload);
    }

    importJson(file, onSuccess, onFail) {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const obj = JSON.parse(String(reader.result || "{}"));
          if (!obj || obj.type !== "drapery") throw new Error("Not a drapery project.");

          const fresh = normalizeState({
            version: obj.version || 1,
            units: obj.units || "in",
            globals: obj.globals || {}, // projectName comes from here
            windows: []
          });

          const defaultPanels = clampPanels12(fresh.globals.defaultPanels, 2);

          fresh.windows = Array.isArray(obj.windows) && obj.windows.length
            ? obj.windows.map(w => ({
                id: w.id || U.uuid(),
                name: w.name || "Window",
                width: normalizeDimension(w.width),
                height: normalizeDimension(getHeightFromWindow(w, "")),
                panels: clampPanels12((w.panels ?? w.o_panels ?? w.fixedPanels), defaultPanels)
              }))
            : [defaultWindow(defaultPanels)];

          this.store.resetTo(fresh);

          if (typeof onSuccess === "function") onSuccess();
        } catch (e) {
          if (typeof onFail === "function") onFail(e);
          else alert("Import failed: " + (e && e.message ? e.message : "Invalid JSON"));
        }
      };
      reader.readAsText(file);
    }

    resetToDefaults() {
      const fresh = normalizeState(this.defaults);
      this.store.resetTo(fresh);
    }
  }

  // ============================================================
  // App / init
  // ============================================================

  function init(el) {
    // GA4 custom events for calculator ROI tracking:
    // - calc_view on page load
    // - calc_add_to_cart on CTA click
    // Uses gtag if present, otherwise pushes to dataLayer.
    function sendGaEvent(name, params) {
      const payload = params || {};
      const debugOn = String(el.dataset.srGaDebug || "") === "1";
      if (debugOn) payload.debug_mode = true;
      if (typeof window.gtag === "function") {
        window.gtag("event", name, payload);
      } else if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push(Object.assign({ event: name }, payload));
      }
    }

    const defaults = window.SRCalc.parseDefaults(el);

    applyIncomingParamsToDefaults(defaults, el);
    const trackingContext = window.SRCalc.createTrackingContext(el, "drapery");
    renderProductContextHeader(el);

    const initialState = normalizeState(defaults);
    const resetBaseline = resetSignature(initialState);
    const store = new StateStore(initialState);
    const calc = new DraperyCalculator();
    const diagramView = new DiagramView();

    const globalsRoot = el.querySelector('[data-role="globals"]');
    const windowsRoot = el.querySelector('[data-role="windows"]');
    const resultsRoot = el.querySelector('[data-role="results"]');

    const ctaRoot = el.querySelector('[data-role="cta"]');

    if (!globalsRoot || !windowsRoot || !resultsRoot) {
      console.error("[sr-calc-drapery] Missing data-role containers inside section.");
      return;
    }

    const resultsView = new ResultsView(resultsRoot, diagramView);
    const globalsView = new GlobalsView(globalsRoot, store, updateResultsOnly);
    const windowsView = new WindowsView(windowsRoot, store, updateResultsOnly, delWindow);

    const io = new ImportExportService(store, defaults);

    function computeAndRenderResults() {
      const project = calc.computeProject(store.getState());
      resultsView.render(project);
      renderCTA(project);
    }

    function updateResultsOnly() {
      computeAndRenderResults();
      updateResetButton();
    }

    function renderAll() {
      globalsView.render();
      windowsView.render();
      computeAndRenderResults();
      updateResetButton();
    }

    // Structure mutations (need full re-render to keep UI consistent)
    function addWindow() {
      store.addWindow();
      renderAll();
    }
    function delWindow(id) {
      store.delWindow(id);
      renderAll();
    }
    function reset() {
      io.resetToDefaults();
      renderAll();
    }
    function renderCTA(project) {
      if (!ctaRoot) return;

      const variantId = Number(el.dataset.srVariant || "");
      const hasVariant = Number.isFinite(variantId) && variantId > 0;

      const avail = Number(el.dataset.srQty || "");
      const hasAvail = Number.isFinite(avail) && avail >= 0;

      const needed = project.totalYards;
      const neededRounded = Math.ceil(needed);

      const unitPriceCents = Number(el.dataset.srPrice || "");
      const hasUnitPrice = Number.isFinite(unitPriceCents) && unitPriceCents > 0;
      const currency = el.dataset.srCurrency || "";
      const unitPrice = hasUnitPrice ? formatMoneyFromCents(unitPriceCents, currency) : "";
      const totalCost = hasUnitPrice ? formatMoneyFromCents(unitPriceCents * neededRounded, currency) : "";

      const enough = !hasAvail ? true : (neededRounded <= avail);
      const canAdd = hasVariant && enough;

      const handle = el.dataset.srHandle || "";
      const backUrl = handle ? `/products/${encodeURIComponent(handle)}${hasVariant ? `?variant=${variantId}` : ""}` : "";
      const calcSource = el.dataset.srSource || "direct";

      if (!project.isComplete) {
        ctaRoot.innerHTML = `
          <div class="sr-calc__cta-box sr-calc__note sr-calc__note--sp12" role="status">
            <b>Enter window width and height</b> to see the required yardage and add it to your cart.
            ${hasAvail ? ` &nbsp;|&nbsp; <b>Available:</b> ${avail} yd` : ``}
          </div>
        `;
        return;
      }

      // If no product context, show guidance instead of cart controls
      if (!hasVariant) {
        ctaRoot.innerHTML = `
          <div class="sr-calc__cta-box sr-calc__note sr-calc__note--sp12">
            <b>Calculator mode:</b> This page isn’t linked to a product, so availability and add-to-cart are disabled.
            <div style="margin-top:8px;">
              <span style="opacity:.9;">Tip:</span>
              Open a product page and click <b>Drapery Yardage Calculator</b> to use the product’s width/repeat and see availability.
              Or keep using this calculator standalone.
            </div>
          </div>
        `;
        return;
      }

      ctaRoot.innerHTML = `
          <div class="sr-calc__cta-box sr-calc__note sr-calc__note--sp12 sr-calc__cta-grid">
            <div class="sr-calc__cta-meta">
              <b>Needed:</b> ${needed.toFixed(2)} yd
            <span style="opacity:.8;">(rounded up: <b>${neededRounded}</b> yd)</span>
            ${hasAvail ? ` &nbsp;|&nbsp; <b>Available:</b> ${avail} yd` : ``}
          </div>

          <div class="sr-calc__cta-actions">
            ${backUrl ? `<a class="sr-calc__btn" href="${backUrl}">View product</a>` : ``}
            ${hasUnitPrice ? `<div class="sr-calc__cta-pricing"><b>Price/yd:</b> ${unitPrice}</div>` : ``}
            ${hasUnitPrice ? `<div class="sr-calc__cta-pricing"><b>Total:</b> ${totalCost}</div>` : ``}
            <button
              class="sr-calc__btn sr-calc__btn--primary"
              type="button"
              data-action="add-to-cart"
              ${canAdd ? "" : "disabled"}
              title="${enough ? "" : "Not enough yardage available for this project"}"
            >
              Add ${neededRounded} yards to cart
            </button>
          </div>

          ${!enough ? `<span class="sr-calc__cta-warning">Not enough stock for ${neededRounded} yd.</span>` : ``}
        </div>
      `;
    }


    // Buttons / import-export
    const btnAdd = el.querySelector('[data-action="add-window"]');
    const btnReset = el.querySelector('[data-action="reset"]');
    const btnExport = el.querySelector('[data-action="export-json"]');
    const btnImport = el.querySelector('[data-action="import-json"]');
    const fileInput = el.querySelector('[data-file="import"]');

    function updateResetButton() {
      if (!btnReset) return;
      const canReset = resetSignature(store.getState()) !== resetBaseline;
      btnReset.disabled = !canReset;
      btnReset.setAttribute("aria-disabled", String(!canReset));
      btnReset.title = canReset ? "Reset calculator" : "Nothing to reset";
    }

    if (btnAdd) btnAdd.addEventListener("click", addWindow);
    if (btnReset) btnReset.addEventListener("click", reset);
    if (btnExport) btnExport.addEventListener("click", () => io.exportJson());

    if (btnImport && fileInput) {
      btnImport.addEventListener("click", () => fileInput.click());
      fileInput.addEventListener("change", () => {
        const f = fileInput.files && fileInput.files[0];
        if (f) {
          io.importJson(
            f,
            () => renderAll(),
            (e) => alert("Import failed: " + (e && e.message ? e.message : "Invalid JSON"))
          );
        }
        fileInput.value = "";
      });
    }

    // CTA: Add-to-cart (independent of import/export)
    if (ctaRoot) {
      ctaRoot.addEventListener("click", async (e) => {
        const btn = e.target && e.target.closest && e.target.closest('[data-action="add-to-cart"]');
        if (!btn) return;
        if (btn.disabled) return;

        const variantId = Number(el.dataset.srVariant || "");
        if (!Number.isFinite(variantId) || variantId <= 0) {
          alert("Missing variant id. Please launch this calculator from a product page.");
          return;
        }

        const project = calc.computeProject(store.getState());
        if (!project.isComplete) return;
        const qty = Math.ceil(project.totalYards);
        const unitPriceCents = Number(el.dataset.srPrice || "");
        const currency = el.dataset.srCurrency || "";

        const tracking = window.SRCalc.buildCartTracking(trackingContext, {
          variantId: variantId,
          productHandle: el.dataset.srHandle || "",
          quantity: qty,
          priceCents: Number.isFinite(unitPriceCents) ? unitPriceCents : 0,
          currency: currency
        });

        btn.disabled = true;
        btn.textContent = "Adding…";

        try {
          const res = await fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", "Accept": "application/json" },
            body: JSON.stringify({
              items: [{
                id: variantId,
                quantity: qty,
                properties: tracking.lineProperties
              }]
            })
          });

          if (!res.ok) throw new Error("Add to cart failed");

          sendGaEvent("calc_add_to_cart", tracking.event);

          window.location.href = "/cart";
        } catch (err) {
          alert("Could not add to cart.");
          btn.disabled = false;
          btn.textContent = `Add ${qty} yd to cart`;
        }
      });
    }

    // Optional: listen to store changes if you later want smarter partial updates.
    // For now we keep behavior identical: inputs update results only.
    store.subscribe((_state, meta) => {
      // No-op; hooks are here for future improvements.
      // meta.kind: globals/window/structure
    });

    renderAll();

    // Fire after initial render so dataset + defaults are in place.
    sendGaEvent("calc_view", {
      variant_id: Number(el.dataset.srVariant || "") || 0,
      product_handle: el.dataset.srHandle || "",
      calc_source: el.dataset.srSource || "direct",
      calc_type: "drapery",
      calc_session_id: trackingContext.sessionId
    });
  }

  document.addEventListener("DOMContentLoaded", () => {
    document.querySelectorAll('[data-sr-calc="drapery"]').forEach(init);
  });
})();
